import React, { Suspense, useState, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';
import PageTransition from './components/PageTransition';
import ScrollToTop from './components/ScrollToTop';
import SmoothScroll from './components/SmoothScroll';
import { AuthProvider } from './contexts/AuthContext';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import { ContactProvider } from './contexts/ContactContext';
import { CustomThemeProvider, useThemeContext } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminPlaceholder from './components/AdminPlaceholder';
import { initializeDatabase } from './services/dbInitializer';
import MaintenancePage from './components/MaintenancePage';
import LogoTest from './components/LogoTest';
import { faviconService } from './services/faviconService';
import ErrorBoundary from './components/ErrorBoundary';
import AdminLayout from './components/AdminLayout';
import SEO from './components/SEO';
import { seoService } from './services/seoService';
import { CssBaseline } from '@mui/material';
import { MessageProvider } from './contexts/MessageContext';

// Lazy load pages for better performance
const HomePage = React.lazy(() => import('./pages/HomePage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const PortfolioPage = React.lazy(() => import('./pages/PortfolioPage'));
const ServicesPage = React.lazy(() => import('./pages/ServicesPage'));
const ToolsPage = React.lazy(() => import('./pages/ToolsPage'));
const ToolPage = React.lazy(() => import('./pages/ToolPage'));
const RentalPage = React.lazy(() => import('./pages/RentalPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const ImageCropDemo = React.lazy(() => import('./pages/ImageCropDemo'));

// Admin pages
const AdminLoginPage = React.lazy(() => import('./pages/AdminLoginPage'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const AdminHomePage = React.lazy(() => import('./pages/AdminHomePage'));
const AdminAboutPage = React.lazy(() => import('./pages/AdminAboutPage'));
const AdminTeamPage = React.lazy(() => import('./pages/AdminTeamPage'));
const AdminSettingsPage = React.lazy(() => import('./pages/AdminSettingsPage'));
const AdminPortfolioPage = React.lazy(() => import('./pages/AdminPortfolioPage'));
const AdminServicesPage = React.lazy(() => import('./pages/AdminServicesPage'));
const AdminToolsPage = React.lazy(() => import('./pages/AdminToolsPage'));
const AdminLightingDatabasePage = React.lazy(() => import('./pages/AdminLightingDatabasePage'));
const AdminMessagesPage = React.lazy(() => import('./pages/AdminMessagesPage'));
const SetupAdminPage = React.lazy(() => import('./pages/SetupAdminPage'));

// Wrap the main app content with settings provider
const AppContent = () => {
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const { settings, loading: settingsLoading } = useSettings();
  const { setIsAdminRoute } = useThemeContext();

  // Set admin route status for theme context
  useEffect(() => {
    setIsAdminRoute(isAdminRoute);
  }, [isAdminRoute, setIsAdminRoute]);

  // Initialize Firebase database
  useEffect(() => {
    // Only initialize in admin routes to avoid unnecessary initialization for regular visitors
    if (isAdminRoute) {
      initializeDatabase().catch(error => {
        console.error('Error initializing database:', error);
      });
    }
  }, [isAdminRoute]);

  // Initialize favicon from company logo
  useEffect(() => {
    if (!settingsLoading && settings.general.logoUrl) {
      // Only attempt to update favicon if there's a logo URL
      faviconService.updateFaviconFromLogo()
        .then(success => {
          if (success) {
            console.log('Favicon automatically updated from company logo');
          }
        })
        .catch(error => {
          console.error('Error automatically updating favicon:', error);
        });
    }
  }, [settings.general.logoUrl, settingsLoading]);

  // Simulate initial loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading || settingsLoading) {
    return <LoadingScreen message="Initializing Paragon Live Design..." />;
  }

  // Check if maintenance mode is enabled and not in admin route
  if (settings.general.maintenanceMode && !isAdminRoute) {
    return <MaintenancePage />;
  }

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <ScrollToTop />
      <SmoothScroll />
      {!isAdminRoute && <Navbar />}
      
      {/* Global SEO - Organization data that appears on all pages */}
      <SEO 
        title={settings.seo.title}
        description={settings.seo.description}
        keywords={settings.seo.keywords}
        image={settings.general.logoUrl}
        structuredData={seoService.getOrganizationStructuredData(settings)}
      />

      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
          pt: isAdminRoute ? 0 : { xs: '56px', sm: '64px' }, // Account for fixed navbar height
        }}
      >
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            {/* Public Routes */}
            <Route path="/" element={
              <PageTransition>
                <ErrorBoundary>
                  <HomePage />
                </ErrorBoundary>
              </PageTransition>
            } />
            
            {/* Conditionally render routes based on visibility settings */}
            {settings.pageVisibility.about && (
              <Route path="/about" element={
                <PageTransition>
                  <ErrorBoundary>
                    <AboutPage />
                  </ErrorBoundary>
                </PageTransition>
              } />
            )}
            
            {settings.pageVisibility.services && (
              <Route path="/services" element={
                <PageTransition>
                  <ErrorBoundary>
                    <ServicesPage />
                  </ErrorBoundary>
                </PageTransition>
              } />
            )}
            
            {settings.pageVisibility.portfolio && (
              <Route path="/portfolio" element={
                <PageTransition>
                  <ErrorBoundary>
                    <PortfolioPage />
                  </ErrorBoundary>
                </PageTransition>
              } />
            )}
            
            {settings.pageVisibility.tools && (
              <Route path="/tools" element={
                <PageTransition>
                  <ErrorBoundary>
                    <ToolsPage />
                  </ErrorBoundary>
                </PageTransition>
              } />
            )}
            
            {/* Individual Tool Route */}
            {settings.pageVisibility.tools && (
              <Route path="/tools/:toolId" element={
                <PageTransition>
                  <ErrorBoundary>
                    <ToolPage />
                  </ErrorBoundary>
                </PageTransition>
              } />
            )}
            
            {settings.pageVisibility.rental && (
              <Route path="/rental" element={
                <PageTransition>
                  <ErrorBoundary>
                    <RentalPage />
                  </ErrorBoundary>
                </PageTransition>
              } />
            )}
            
            {settings.pageVisibility.contact && (
              <Route path="/contact" element={
                <PageTransition>
                  <ErrorBoundary>
                    <ContactPage />
                  </ErrorBoundary>
                </PageTransition>
              } />
            )}
            
            {/* Image Crop Demo Route */}
            <Route path="/demo/image-crop" element={
              <PageTransition>
                <ErrorBoundary>
                  <ImageCropDemo />
                </ErrorBoundary>
              </PageTransition>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin/setup" element={
              <Suspense fallback={<LoadingScreen />}>
                <ErrorBoundary>
                  <SetupAdminPage />
                </ErrorBoundary>
              </Suspense>
            } />
            <Route path="/admin/login" element={
              <ErrorBoundary>
                <AdminLoginPage />
              </ErrorBoundary>
            } />
            
            {/* Test Route for Logo */}
            <Route path="/test/logo" element={
              <ErrorBoundary>
                <LogoTest />
              </ErrorBoundary>
            } />
            
            {/* Redirect /admin to /admin/dashboard */}
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            
            <Route path="/admin/*" element={
              <ErrorBoundary errorComponent="detailed">
                <ProtectedRoute>
                  <AdminLayout>
                    <Routes>
                      <Route path="dashboard" element={<AdminDashboard />} />
                      <Route path="home" element={<AdminHomePage />} />
                      <Route path="about" element={<AdminAboutPage />} />
                      <Route path="team" element={<AdminTeamPage />} />
                      <Route path="portfolio" element={<AdminPortfolioPage />} />
                      <Route path="tools" element={<AdminToolsPage />} />
                      <Route path="lighting-database" element={<AdminLightingDatabasePage />} />
                      <Route path="services" element={<AdminServicesPage />} />
                      <Route path="messages" element={<AdminMessagesPage />} />
                      <Route path="rental" element={<AdminPlaceholder 
                        title="Rental Inventory" 
                        description="This section will allow you to manage your rental equipment inventory, including availability, pricing, and specifications."
                      />} />
                      <Route path="settings" element={<AdminSettingsPage />} />
                    </Routes>
                  </AdminLayout>
                </ProtectedRoute>
              </ErrorBoundary>
            } />
            
            {/* 404 Route - Always keep it last */}
            <Route path="*" element={
              <PageTransition>
                <NotFoundPage />
              </PageTransition>
            } />
          </Routes>
        </AnimatePresence>
      </Box>
      
      {!isAdminRoute && <Footer />}
    </Box>
  );
};

function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <AuthProvider>
          <CustomThemeProvider>
            <CssBaseline />
            <SettingsProvider>
              <ContactProvider>
                <MessageProvider>
                  <Suspense fallback={<LoadingScreen />}>
                    <AppContent />
                  </Suspense>
                </MessageProvider>
              </ContactProvider>
            </SettingsProvider>
          </CustomThemeProvider>
        </AuthProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
